import { Check, ChevronDown, HandPlatter, Wrench } from "lucide-react";
import objectHash from "object-hash";
import { useContext, useEffect, useState } from "react";

import { QuestionMarkCircle } from "./icons";
import { Button } from "./ui/button";
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from "./ui/command";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import { UserContext } from "@/contexts/UserContext";
import sendStandardEvent from "@/hooks/use-firebase-event";
import useReadOnly from "@/hooks/use-readonly";
import { cn } from "@/lib/utils";
import { emptySettings } from "@/slices/settings";
import { useGetSettingsQuery } from "@/store/enhancedApi";
import {
  IdentityProviderType,
  SettingsProperties,
  useCheckIdentityProviderSettingsMutation,
  useUpdateSettingsMutation,
} from "@/store/pomeriumApi";
import {
  IdentityProviders,
  IdentityProvidersLookup,
} from "@/views/settings/components/IdentityProviders";

type IdentityProviderSettingsProps = {
  clusterId: string | undefined;
  organizationId: string | undefined;
  setEnableFinishStep?: (value: boolean) => void;
};

export default function IdentityProviderSettings({
  organizationId,
  clusterId,
}: IdentityProviderSettingsProps) {
  const { user } = useContext(UserContext);
  const readonly = useReadOnly({ organizationId, clusterId });
  const { data: settings } = useGetSettingsQuery(
    {
      organizationId: organizationId || "",
      clusterId: clusterId || "",
    },
    {
      skip: !organizationId || !clusterId,
    },
  );

  const [mode, setMode] = useState<"hosted" | "custom">("hosted");
  const [settingsForm, setSettingsForm] = useState<SettingsProperties>({
    ...emptySettings,
    address: ":443",
  });
  const [updateSettings, { isLoading: isSettingsUpdating }] =
    useUpdateSettingsMutation();

  const checkIdentityProviderSettingsRequest = {
    provider: settingsForm.identityProvider as IdentityProviderType,
    clientId: settingsForm.identityProviderClientId,
    clientSecret: settingsForm.identityProviderClientSecret,
    requestParams: settingsForm.identityProviderRequestParams,
    scopes: settingsForm.identityProviderScopes,
    url: settingsForm.identityProviderUrl,
  };

  const [
    checkIdentityProviderSettings,
    { data: checkIdentityProviderSettingsResult },
  ] = useCheckIdentityProviderSettingsMutation();

  useEffect(() => {
    setSettingsForm(settings || { ...emptySettings, address: ":443" });
    if (settings?.identityProvider) {
      setMode("custom");
    }
  }, [settings]);

  useEffect(() => {
    if (mode === "hosted" && settingsForm.identityProvider) {
      setSettingsForm({ ...emptySettings, address: ":443" });
    } else if (mode === "custom" && settings?.identityProvider) {
      setSettingsForm(settings);
    }
  }, [mode]);

  useEffect(() => {
    if (settingsForm.identityProvider) {
      checkIdentityProviderSettings(checkIdentityProviderSettingsRequest);
    }
  }, [settingsForm]);

  async function saveSettings() {
    if (organizationId && clusterId && settingsForm) {
      const updated = await updateSettings({
        organizationId,
        clusterId,
        settingsProperties: settingsForm,
      }).unwrap();
      setSettingsForm(updated);
    }
  }

  return (
    <>
      <RadioGroup
        defaultValue="hosted"
        value={mode}
        onValueChange={(value: string) => setMode(value as "hosted" | "custom")}
        className="w-full grid grid-cols-1 xl:grid-cols-2 gap-2"
      >
        <Label
          className={cn(
            "relative flex flex-row gap-2 border rounded-lg px-3 py-2",
            mode === "hosted" && "border-primary",
          )}
        >
          <RadioGroupItem value="hosted" className="peer" />
          <HandPlatter className="size-3" />
          Hosted Authenticate Service
        </Label>

        <Label
          className={cn(
            "relative flex flex-row gap-2 border rounded-lg px-3 py-2",
            mode === "custom" && "border-primary",
          )}
        >
          <RadioGroupItem value="custom" className="peer" />
          <Wrench className="size-3" />
          Custom Identity Provider
        </Label>
      </RadioGroup>

      {mode === "hosted" && (
        <p className="text-center">
          The Hosted Authenticate Service is an identity provider you can start
          using right away. It's good for hobbyist deployments and for getting
          started with Pomerium.
        </p>
      )}

      {mode === "custom" && (
        <>
          <h3 className="font-semibold">
            Setup your Identity Provider Settings
          </h3>
          <span className="space-y-3 lg:space-x-3">
            <Label htmlFor="idp">Identity Provider</Label>
            <IdentityProviderCombobox
              id="idp"
              value={settingsForm.identityProvider || ""}
              onChange={(value) => {
                setSettingsForm({
                  ...settingsForm,
                  identityProvider: value as IdentityProviderType,
                  identityProviderUrl: undefined,
                });
              }}
              readonly={readonly}
            />
            {settingsForm.identityProvider && (
              <Button
                variant="secondary"
                onClick={() =>
                  sendStandardEvent("Clicked support", "idpSettings", user)
                }
                asChild
              >
                <a
                  href={
                    IdentityProvidersLookup.get(settingsForm.identityProvider)
                      ?.docs || ""
                  }
                  className="no-underline"
                >
                  <QuestionMarkCircle color="info" />I need help with{" "}
                  {IdentityProvidersLookup.get(settingsForm.identityProvider)
                    ?.name || ""}
                </a>
              </Button>
            )}
          </span>

          {settingsForm.identityProvider && (
            <>
              {IdentityProvidersLookup.get(settingsForm.identityProvider || "")
                ?.providerUrl !== "restricted" && (
                <Label htmlFor="idpUrl" className="flex flex-col items-start">
                  <span className="space-y-3">Identity Provider URL</span>
                  <Input
                    id="idpUrl"
                    type="text"
                    value={settingsForm?.identityProviderUrl}
                    placeholder=""
                    onChange={(e) =>
                      setSettingsForm({
                        ...settingsForm,
                        identityProviderUrl: e.target.value,
                      })
                    }
                    readOnly={readonly}
                    aria-invalid={Boolean(
                      checkIdentityProviderSettingsResult?.errors?.url,
                    )}
                  />
                  {checkIdentityProviderSettingsResult?.errors?.url && (
                    <span className="text-xs text-destructive">
                      {checkIdentityProviderSettingsResult.errors.url[0].toUpperCase() +
                        checkIdentityProviderSettingsResult.errors.url.substring(
                          1,
                        )}
                    </span>
                  )}
                </Label>
              )}
              <Label htmlFor="clientId" className="flex flex-col items-start">
                <span className="space-y-3">Client ID</span>
                <Input
                  id="clientId"
                  type="text"
                  value={settingsForm?.identityProviderClientId}
                  onChange={(e) =>
                    setSettingsForm({
                      ...settingsForm,
                      identityProviderClientId: e.target.value,
                    })
                  }
                  aria-invalid={Boolean(
                    checkIdentityProviderSettingsResult?.errors?.clientId,
                  )}
                  readOnly={readonly}
                />
                {checkIdentityProviderSettingsResult?.errors?.clientId && (
                  <span className="text-xs text-destructive">
                    {checkIdentityProviderSettingsResult.errors.clientId[0].toUpperCase() +
                      checkIdentityProviderSettingsResult.errors.clientId.substring(
                        1,
                      )}
                  </span>
                )}
              </Label>
              <Label
                htmlFor="clientSecret"
                className="flex flex-col items-start"
              >
                <span className="space-y-3">Client Secret</span>
                <Input
                  id="clientSecret"
                  type="password"
                  value={settingsForm?.identityProviderClientSecret}
                  onChange={(e) =>
                    setSettingsForm({
                      ...settingsForm,
                      identityProviderClientSecret: e.target.value,
                    })
                  }
                  aria-invalid={Boolean(
                    checkIdentityProviderSettingsResult?.errors?.clientSecret,
                  )}
                  readOnly={readonly}
                />
                {checkIdentityProviderSettingsResult?.errors?.clientSecret && (
                  <span className="text-xs text-destructive">
                    {checkIdentityProviderSettingsResult.errors.clientSecret[0].toUpperCase() +
                      checkIdentityProviderSettingsResult.errors.clientSecret.substring(
                        1,
                      )}
                  </span>
                )}
              </Label>
              <span className="space-y-3">
                <Label htmlFor="authServiceUrl">Authenticate Service URL</Label>
                <Input
                  id="authServiceUrl"
                  type="text"
                  value={settingsForm?.authenticateServiceUrl}
                  onChange={(e) =>
                    setSettingsForm({
                      ...settingsForm,
                      authenticateServiceUrl: e.target.value,
                    })
                  }
                  readOnly={readonly}
                />
              </span>
            </>
          )}
        </>
      )}

      <Button
        variant="default"
        onClick={saveSettings}
        disabled={
          !!checkIdentityProviderSettingsResult?.errors ||
          (settings
            ? objectHash(settingsForm) === objectHash(settings)
            : isSettingsUpdating)
        }
      >
        Save
      </Button>
    </>
  );
}

type IdentityProviderComboboxProps = {
  id: string;
  value: IdentityProviderType | "";
  onChange: (value: string) => void;
  readonly: boolean;
};

function IdentityProviderCombobox({
  id,
  value,
  onChange,
  readonly,
}: IdentityProviderComboboxProps) {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  return (
    <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
      <PopoverTrigger
        id={id}
        disabled={readonly}
        role="combobox"
        aria-expanded={isPopoverOpen}
        className="w-full border rounded-lg px-3 py-2 text-start flex justify-between items-center"
      >
        {IdentityProvidersLookup.get(value)?.name || "Select a provider..."}
        <ChevronDown className="size-3 dark:text-muted" />
      </PopoverTrigger>
      <PopoverContent className="w-full">
        <Command>
          <CommandInput placeholder="Search..." />
          <CommandList>
            <CommandEmpty>No matching provider.</CommandEmpty>
            {IdentityProviders.map((provider) => (
              <CommandItem
                key={provider.id}
                value={provider.id}
                onSelect={(value) => {
                  onChange(value);
                  setIsPopoverOpen(false);
                }}
                className="flex justify-between"
              >
                {provider.name}
                {value === provider.id && <Check className="size-3" />}
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
